import {
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  List,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useState } from "react";
import { useTagsManager } from "../Hooks/useTagsManager";
import { TagFormat, TagType } from "../../common/models/schema/TagFormat";
import TagItem from "../Components/TagItem";
import CreateTagForm from "../Components/CreateTagForm";

const TagsManagerPage = () => {
  const {
    tags,
    loading,
    error,
    createError,
    updateError,
    createTag,
    updateTag,
    deleteTag,
  } = useTagsManager();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<number | null>(null);
  const [editingTag, setEditingTag] = useState<TagFormat | null>(null);

  const handleUpdateTag = async () => {
    if (!editingTag) return;

    try {
      await updateTag(editingTag);
      setEditingTag(null);
    } catch (error) {
      console.error("Error updating tag:", error);
    }
  };

  const handleInputChange = (field: string, value: string | TagType) => {
    if (editingTag) {
      setEditingTag((prev) => (prev ? { ...prev, [field]: value } : null));
    }
  };

  const handleDeleteClick = (id: number) => {
    setTagToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (tagToDelete) {
      try {
        await deleteTag(tagToDelete);
        setDeleteDialogOpen(false);
        setTagToDelete(null);
      } catch (error) {
        console.error("Error deleting tag:", error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTagToDelete(null);
  };

  const handleEditClick = (tag: TagFormat) => {
    setEditingTag({ ...tag });
  };

  const handleEditCancel = () => {
    setEditingTag(null);
  };

  const getTagTypeLabel = (type: TagType): string => {
    switch (type) {
      case TagType.LIST:
        return "List";
      case TagType.STRING:
        return "String";
      default:
        return "String";
    }
  };

  const getTagToDeleteName = (): string => {
    const tag = tags.find((t) => t.id === tagToDelete);
    return tag ? tag.name : "";
  };

  return (
    <Box sx={{ p: 3 }}>
      <CreateTagForm
        onCreateTag={createTag}
        createError={createError}
        getTagTypeLabel={getTagTypeLabel}
      />

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && tags.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <List>
            {tags.map((tag: TagFormat, index: number) => (
              <Box key={tag.id}>
                <TagItem
                  tag={tag}
                  editingTag={editingTag}
                  updateError={updateError}
                  onEditClick={handleEditClick}
                  onEditCancel={handleEditCancel}
                  onUpdateTag={handleUpdateTag}
                  onDeleteClick={handleDeleteClick}
                  onInputChange={handleInputChange}
                  getTagTypeLabel={getTagTypeLabel}
                />
                {index < tags.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        </Box>
      )}

      {!loading && !error && tags.length === 0 && (
        <Typography variant="body1" sx={{ mt: 3 }}>
          No tags found.
        </Typography>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Tag</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete tag "{getTagToDeleteName()}"? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TagsManagerPage;
